import { forwardRef, useState, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Badge,
  Avatar,
  ActionIcon,
  Menu,
  Paper,
  ScrollArea,
  Tooltip,
} from '@mantine/core';
import {
  IconPlus,
  IconDots,
  IconEdit,
  IconTrash,
  IconGripVertical,
} from '@tabler/icons-react';
// Card removed - columns now use transparent Box
import { FadeIn, HoverLift } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface KanbanItem {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assignee?: {
    id: string;
    name: string;
    avatar?: string;
  };
  dueDate?: Date;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface KanbanColumn {
  key: string;
  title: string;
  color?: string;
  limit?: number;
  icon?: ReactNode;
}

export interface KanbanBoardProps {
  columns: KanbanColumn[];
  items: KanbanItem[];
  onItemMove?: (itemId: string, fromColumn: string, toColumn: string) => void;
  onItemClick?: (item: KanbanItem) => void;
  onItemEdit?: (item: KanbanItem) => void;
  onItemDelete?: (item: KanbanItem) => void;
  onAddItem?: (columnKey: string) => void;
  renderItem?: (item: KanbanItem) => ReactNode;
  showAddButton?: boolean;
  showColumnCount?: boolean;
  className?: string;
}

export const KanbanBoard = forwardRef<HTMLDivElement, KanbanBoardProps>(
  (
    {
      columns,
      items = [],
      onItemMove,
      onItemClick,
      onItemEdit,
      onItemDelete,
      onAddItem,
      renderItem,
      showAddButton = true,
      showColumnCount = true,
      className,
    },
    ref
  ) => {
    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const getColumnItems = (columnKey: string) =>
      items.filter((item) => item.status === columnKey);

    const getPriorityColor = (priority?: string) => {
      switch (priority) {
        case 'urgent':
          return 'red';
        case 'high':
          return 'orange';
        case 'medium':
          return 'yellow';
        case 'low':
          return 'green';
        default:
          return 'gray';
      }
    };

    const handleDragStart = (e: React.DragEvent, itemId: string) => {
      setDraggedItem(itemId);
      e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = () => {
      setDraggedItem(null);
      setDragOverColumn(null);
    };

    const handleDragOver = (e: React.DragEvent, columnKey: string) => {
      e.preventDefault();
      setDragOverColumn(columnKey);
    };

    const handleDrop = (e: React.DragEvent, toColumn: string) => {
      e.preventDefault();
      if (!draggedItem) return;

      const item = items.find((i) => i.id === draggedItem);
      if (item && item.status !== toColumn) {
        onItemMove?.(draggedItem, item.status, toColumn);
      }

      setDraggedItem(null);
      setDragOverColumn(null);
    };

    const formatDate = (date?: Date) => {
      if (!date) return '';
      const d = new Date(date);
      const today = new Date();
      const isOverdue = d < today;

      return (
        <Text size="xs" c={isOverdue ? 'red' : 'dimmed'} style={accessibilityStyles}>
          {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </Text>
      );
    };

    const renderDefaultItem = (item: KanbanItem) => (
      <HoverLift lift={4}>
        <Paper
          p="sm"
          withBorder
          draggable
          onDragStart={(e) => handleDragStart(e, item.id)}
          onDragEnd={handleDragEnd}
          style={{
            cursor: 'grab',
            opacity: draggedItem === item.id ? 0.5 : 1,
          }}
          onClick={() => onItemClick?.(item)}
        >
          <Stack gap="xs">
            {/* Header */}
            <Group justify="space-between" wrap="nowrap">
              <Group gap={4}>
                <IconGripVertical size={14} color="gray" />
                {item.priority && (
                  <Tooltip label={`${item.priority} priority`}>
                    <Box
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: `var(--mantine-color-${getPriorityColor(item.priority)}-5)`,
                      }}
                    />
                  </Tooltip>
                )}
              </Group>
              {(onItemEdit || onItemDelete) && (
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconDots size={14} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {onItemEdit && (
                      <Menu.Item
                        leftSection={<IconEdit size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onItemEdit(item);
                        }}
                      >
                        Edit
                      </Menu.Item>
                    )}
                    {onItemDelete && (
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          onItemDelete(item);
                        }}
                      >
                        Delete
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>

            {/* Title */}
            <Text size="sm" fw={500} lineClamp={2} style={accessibilityStyles}>
              {item.title}
            </Text>

            {/* Description */}
            {item.description && (
              <Text size="xs" c="dimmed" lineClamp={2} style={accessibilityStyles}>
                {item.description}
              </Text>
            )}

            {/* Tags */}
            {item.tags && item.tags.length > 0 && (
              <Group gap={4}>
                {item.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag} size="xs" variant="light">
                    {tag}
                  </Badge>
                ))}
                {item.tags.length > 3 && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>+{item.tags.length - 3}</Text>
                )}
              </Group>
            )}

            {/* Footer */}
            <Group justify="space-between">
              {item.assignee && (
                <Tooltip label={item.assignee.name}>
                  <Avatar src={item.assignee.avatar} size="xs" radius="xl">
                    {item.assignee.name.charAt(0)}
                  </Avatar>
                </Tooltip>
              )}
              {item.dueDate && formatDate(item.dueDate)}
            </Group>
          </Stack>
        </Paper>
      </HoverLift>
    );

    return (
      <FadeIn>
        <Box ref={ref} className={className} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <ScrollArea style={{ flex: 1 }}>
            <Group gap="md" align="stretch" wrap="nowrap" grow style={{ minWidth: 'fit-content', minHeight: 'calc(100vh - 220px)' }}>
              {columns.map((column) => {
                const columnItems = getColumnItems(column.key);
                const isOverLimit = column.limit && columnItems.length > column.limit;
                const isDragOver = dragOverColumn === column.key;

                return (
                  <Box
                    key={column.key}
                    p="sm"
                    style={{
                      flex: 1,
                      minWidth: 250,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 'var(--mantine-radius-md)',
                      backgroundColor: isDragOver
                        ? 'var(--mantine-color-blue-0)'
                        : 'transparent',
                      border: isDragOver
                        ? '2px dashed var(--mantine-color-blue-4)'
                        : '2px dashed transparent',
                      transition: 'background-color 0.2s, border-color 0.2s',
                    }}
                    onDragOver={(e: React.DragEvent) => handleDragOver(e, column.key)}
                    onDrop={(e: React.DragEvent) => handleDrop(e, column.key)}
                  >
                    <Stack gap="sm" style={{ flex: 1 }}>
                      {/* Column header */}
                      <Group justify="space-between">
                        <Group gap="xs">
                          {column.icon}
                          <Text fw={600} style={accessibilityStyles}>{column.title}</Text>
                          {showColumnCount && (
                            <Badge
                              size="sm"
                              variant="light"
                              color={isOverLimit ? 'red' : column.color || 'gray'}
                            >
                              {columnItems.length}
                              {column.limit && `/${column.limit}`}
                            </Badge>
                          )}
                        </Group>
                        {showAddButton && onAddItem && (
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            onClick={() => onAddItem(column.key)}
                          >
                            <IconPlus size={16} />
                          </ActionIcon>
                        )}
                      </Group>

                      {/* Items */}
                      <ScrollArea.Autosize mah="calc(100vh - 300px)" style={{ flex: 1 }}>
                        <Stack gap="xs">
                          {columnItems.map((item) => (
                            <Box
                              key={item.id}
                              draggable={!!renderItem}
                              onDragStart={renderItem ? (e: React.DragEvent) => handleDragStart(e, item.id) : undefined}
                              onDragEnd={renderItem ? handleDragEnd : undefined}
                              style={renderItem ? { cursor: 'grab', opacity: draggedItem === item.id ? 0.5 : 1 } : undefined}
                            >
                              {renderItem ? renderItem(item) : renderDefaultItem(item)}
                            </Box>
                          ))}
                          {columnItems.length === 0 && (
                            <Paper
                              p="md"
                              style={{
                                border: '2px dashed var(--mantine-color-gray-3)',
                                backgroundColor: 'transparent',
                              }}
                            >
                              <Text size="sm" c="dimmed" ta="center" style={accessibilityStyles}>
                                No items
                              </Text>
                            </Paper>
                          )}
                        </Stack>
                      </ScrollArea.Autosize>
                    </Stack>
                  </Box>
                );
              })}
            </Group>
          </ScrollArea>
        </Box>
      </FadeIn>
    );
  }
);

KanbanBoard.displayName = 'KanbanBoard';

export default KanbanBoard;
